import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Activity, Mail, Send, Zap, Heart, AlertTriangle, AlertOctagon } from "lucide-react";

const BUCKET_META = {
  healthy:  { label: "Saludable",  color: "#10b981", bg: "bg-[#10b981]/10",  border: "border-[#10b981]/40",  Icon: Heart },
  warning:  { label: "Atención",   color: "#f59e0b", bg: "bg-[#f59e0b]/10",  border: "border-[#f59e0b]/40",  Icon: AlertTriangle },
  at_risk:  { label: "En riesgo",  color: "#7248ae", bg: "bg-[#7248ae]/10",  border: "border-[#7248ae]/40",  Icon: Activity },
  critical: { label: "Crítico",    color: "#e11d48", bg: "bg-[#e11d48]/10",  border: "border-[#e11d48]/40",  Icon: AlertOctagon },
};

export default function OwnerChurn() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/churn"); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runSweep = async (force = false) => {
    setRunning(true);
    try {
      const { data: res } = await api.post(`/owner/churn/sweep${force ? "?force=true" : ""}`);
      const failed = res.failed || 0;
      toast.success(`Sweep: ${res.sent} enviados · ${res.skipped} omitidos${failed ? ` · ${failed} fallidos` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const sendOne = async (admin) => {
    try {
      await api.post(`/owner/churn/${admin.id}/send`);
      toast.success(`Correo enviado a ${admin.email}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <p className="text-[#7248ae]">Calculando scores…</p>;
  if (!data) return null;

  const rows = filter === "all" ? data.admins : data.admins.filter(r => r.bucket === filter);

  return (
    <div className="animate-fade-up" data-testid="owner-churn-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Retención automática</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Score de churn</h1>
          <p className="text-[#7248ae] mt-2 max-w-2xl">
            Score 0-100 calculado a partir de días sin pedidos, días al vencimiento, caída de ventas y volumen mensual.
            Los admins en <strong>at_risk</strong> y <strong>crítico</strong> reciben un correo automático con sugerencias cada 5 días.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => runSweep(false)} disabled={running} variant="outline" className="border-[#b182ec]/50" data-testid="run-sweep-button">
            <Zap className="h-4 w-4 mr-2" />Ejecutar sweep
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={running} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="force-sweep-button">
                <Send className="h-4 w-4 mr-2" />Forzar a todos
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Forzar envío masivo?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esto enviará un correo de churn a <strong>todos los administradores</strong> sin importar su bucket ni el cooldown de 5 días. Úsalo solo cuando quieras una comunicación de retención inmediata.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction className="bg-[#3e166c] hover:bg-[#7248ae]" onClick={() => runSweep(true)} data-testid="confirm-force-button">Sí, forzar envío</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.keys(BUCKET_META).map((key, i) => {
          const m = BUCKET_META[key];
          const Icon = m.Icon;
          const count = data.summary[key] || 0;
          const active = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(active ? "all" : key)}
              className={`p-5 rounded-2xl border text-left transition-all hover:-translate-y-1 animate-fade-up stagger-${i+1} ${active ? `${m.bg} ${m.border}` : "bg-white border-[#b182ec]/30"}`}
              data-testid={`bucket-filter-${key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider" style={{ color: m.color }}>{m.label}</p>
                <Icon className="h-5 w-5" style={{ color: m.color }} />
              </div>
              <p className="font-display text-3xl font-black text-[#1a0f2e]">{count}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#7248ae] mt-1">{count === 1 ? "cuenta" : "cuentas"}</p>
            </button>
          );
        })}
      </div>

      {filter !== "all" && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#7248ae]">Filtrando por <strong className="text-[#1a0f2e]">{BUCKET_META[filter].label}</strong></p>
          <Button size="sm" variant="ghost" onClick={() => setFilter("all")} className="text-[#3e166c]">Ver todas</Button>
        </div>
      )}

      {rows.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-[#b182ec]/40">
          <p className="text-[#7248ae]">No hay cuentas con ese estado.</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="churn-cards">
          {rows.map((r, idx) => {
            const m = BUCKET_META[r.bucket] || BUCKET_META.healthy;
            const Icon = m.Icon;
            const meterPct = Math.min(100, r.score);
            return (
              <Card key={r.id} className={`p-6 border ${m.border} rounded-2xl card-glow transition-all hover:-translate-y-0.5 animate-fade-up stagger-${(idx%6)+1}`} data-testid={`churn-row-${r.email}`}>
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-display text-2xl font-bold text-[#1a0f2e]">{r.name}</h3>
                      <Badge className="text-white" style={{ background: m.color }}>
                        <Icon className="h-3 w-3 mr-1" />{m.label}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#7248ae]">{r.restaurant_name} · {r.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-1">Score</p>
                    <p className="font-display text-4xl font-black" style={{ color: m.color }} data-testid={`churn-score-${r.email}`}>{r.score}<span className="text-base text-[#7248ae] font-normal">/100</span></p>
                  </div>
                </div>

                {/* Score meter */}
                <div className="h-2 w-full rounded-full bg-[#b182ec]/15 mb-4 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${meterPct}%`, background: m.color }} />
                </div>

                {/* Signals */}
                {r.signals && r.signals.length > 0 && (
                  <div className="mb-4" data-testid={`signals-${r.email}`}>
                    <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-2">Señales detectadas</p>
                    <ul className="space-y-1">
                      {r.signals.map((s, i) => (
                        <li key={i} className="text-sm text-[#1a0f2e] flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: m.color }} />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {r.suggested_action && (
                  <div className={`p-3 rounded-xl ${m.bg} mb-4`}>
                    <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-1">Sugerencia</p>
                    <p className="text-sm text-[#1a0f2e]">{r.suggested_action}</p>
                  </div>
                )}

                {/* Metrics row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-[#7248ae] mb-4">
                  <div>
                    <p className="uppercase tracking-wider">Días sin pedido</p>
                    <p className="text-[#1a0f2e] font-semibold text-base">
                      {r.metrics?.days_since_last_order != null ? r.metrics.days_since_last_order : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wider">Ventas 7d</p>
                    <p className="text-[#1a0f2e] font-semibold text-base">{formatCurrency(r.metrics?.revenue_7d || 0)}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wider">Pedidos 30d</p>
                    <p className="text-[#1a0f2e] font-semibold text-base">{r.metrics?.orders_30d || 0}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wider">Vencimiento</p>
                    <p className="text-[#1a0f2e] font-semibold text-base">
                      {r.metrics?.subscription_active ? `${r.metrics?.days_left}d` : "Vencido"}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#b182ec]/20 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-[#7248ae]">
                    {r.last_churn_email_at ? (
                      <>Último correo automático: <strong className="text-[#1a0f2e]">{formatDate(r.last_churn_email_at)}</strong></>
                    ) : (
                      <em>Sin correos automáticos enviados</em>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" asChild className="border-[#b182ec]/50" data-testid={`mail-${r.email}`}>
                      <a href={`mailto:${r.email}?subject=oRdeN%20-%20Hablemos`}>
                        <Mail className="h-3 w-3 mr-1" />Personal
                      </a>
                    </Button>
                    <Button size="sm" onClick={() => sendOne(r)} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid={`send-${r.email}`}>
                      <Send className="h-3 w-3 mr-1" />Enviar churn ahora
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
